import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Store, Upload, Trash2, Video, Loader2, ExternalLink, Globe, EyeOff, Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShopPublish({ bicycle }) {
  const [shop, setShop] = useState(null);
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const photoRef = useRef(null);
  const videoRef = useRef(null);

  const load = async () => {
    const r = await axios.get(`${API}/shop/bikes/${bicycle.id}`);
    setShop(r.data);
    setPrice(r.data.price ?? bicycle.manual_sale_price ?? '');
    setDescription(r.data.description || '');
  };
  useEffect(() => { load().catch(() => {}); }, [bicycle.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (payload, okMsg) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/shop/bikes/${bicycle.id}`, payload);
      setShop(r.data); toast.success(okMsg);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    finally { setBusy(false); }
  };

  const togglePublish = (checked) => save(
    { published: !!checked, price: price ? parseFloat(price) : undefined, description },
    checked ? 'Bicicleta publicada en la Tienda Online' : 'Bicicleta retirada de la Tienda Online'
  );

  const uploadPhotos = async (files) => {
    if (!files?.length) return;
    const fd = new FormData(); Array.from(files).forEach((f) => fd.append('files', f));
    setBusy(true);
    try { const r = await axios.post(`${API}/shop/bikes/${bicycle.id}/photos`, fd); setShop(r.data); toast.success('Fotos añadidas'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error al subir fotos'); }
    finally { setBusy(false); }
  };
  const deletePhoto = async (i) => {
    try { const r = await axios.delete(`${API}/shop/bikes/${bicycle.id}/photos/${i}`); setShop(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar la foto'); }
  };
  const uploadVideo = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    setBusy(true);
    try { const r = await axios.post(`${API}/shop/bikes/${bicycle.id}/video`, fd); setShop(r.data); toast.success('Vídeo guardado'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error al subir el vídeo'); }
    finally { setBusy(false); }
  };
  const deleteVideo = async () => {
    try { const r = await axios.delete(`${API}/shop/bikes/${bicycle.id}/video`); setShop(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar el vídeo'); }
  };

  if (!shop) return null;
  const publicUrl = `${window.location.origin}/tienda/bici/${bicycle.id}`;

  return (
    <Card className="border-orange-200 bg-orange-50/40" data-testid="shop-publish-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2 text-orange-900">
          <Store size={18} /> Tienda Online
        </CardTitle>
        <div className="flex items-center gap-2">
          {shop.reserved && <Badge className="bg-blue-100 text-blue-700 border-blue-300 gap-1"><Lock size={12} /> Reservada</Badge>}
          {shop.published ? (
            shop.visible ? <Badge className="bg-green-100 text-green-700 border-green-300 gap-1"><Globe size={12} /> Visible en tienda</Badge>
              : <Badge className="bg-slate-100 text-slate-600 border-slate-300 gap-1"><EyeOff size={12} /> Publicada (oculta por estado)</Badge>
          ) : <Badge variant="outline" className="text-slate-500">No publicada</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox checked={!!shop.published} onCheckedChange={togglePublish} disabled={busy} data-testid="shop-publish-checkbox" />
          <span className="text-sm font-medium text-slate-800">Publicar en Tienda Online</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Precio de venta (€)</Label>
            <Input type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" className="bg-white" data-testid="shop-price-input" />
            {bicycle.manual_sale_price && <p className="text-[11px] text-slate-500">Precio 2ª mano en Veloflux: {bicycle.manual_sale_price} €</p>}
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs">Descripción personalizada</Label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="w-full rounded-md border border-slate-200 bg-white p-2 text-sm" placeholder="Texto que verán los visitantes en la ficha pública…" data-testid="shop-description-input" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => save({ price: price ? parseFloat(price) : undefined, description }, 'Datos de tienda guardados')} disabled={busy} className="bg-orange-500 hover:bg-orange-600" data-testid="shop-save-btn">Guardar precio y descripción</Button>
          {shop.reserved && <Button size="sm" variant="outline" onClick={() => save({ reserved: false }, 'Reserva liberada')} data-testid="shop-release-btn">Liberar reserva</Button>}
          {shop.visible && <a href={publicUrl} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="gap-1" data-testid="shop-open-public"><ExternalLink size={14} /> Ver ficha pública</Button></a>}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs">Fotos adicionales ({shop.photos.length}) · la foto principal de la bici se muestra primero</Label>
            <input ref={photoRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => { uploadPhotos(e.target.files); e.target.value = ''; }} data-testid="shop-photos-input" />
            <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => photoRef.current?.click()} disabled={busy} data-testid="shop-add-photos-btn">{busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Añadir fotos</Button>
          </div>
          {shop.photos.length > 0 && (
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {shop.photos.map((p, i) => (
                <div key={p} className="relative group aspect-[4/3] rounded-md overflow-hidden border border-slate-200 bg-white" data-testid={`shop-photo-${i}`}>
                  <img src={p} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => deletePhoto(i)} className="absolute top-1 right-1 bg-white/90 rounded p-1 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`shop-photo-delete-${i}`}><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <input ref={videoRef} type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={(e) => { uploadVideo(e.target.files?.[0]); e.target.value = ''; }} data-testid="shop-video-input" />
          <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => videoRef.current?.click()} disabled={busy} data-testid="shop-video-btn"><Video size={12} /> {shop.video_url ? 'Sustituir vídeo' : 'Añadir vídeo (MP4, máx. 60MB)'}</Button>
          {shop.video_url && (
            <>
              <a href={shop.video_url} target="_blank" rel="noreferrer" className="text-xs text-violet-700 underline" data-testid="shop-video-link">Ver vídeo</a>
              <Button size="sm" variant="ghost" className="h-7 text-red-600 text-xs" onClick={deleteVideo} data-testid="shop-video-delete"><Trash2 size={12} /></Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
